import { Separator } from '@workspace/ui/components/separator'
import { Lightbulb, Mic, Play, Target } from 'lucide-react'
import { DataSourcesSection } from './components/data-sources-section'
import { PitchCta } from './components/pitch-cta'
import { PreviousPitchesSection } from './components/previous-pitches-section'
import { ProfileSummary } from './components/profile-summary'
import type { DataSource, PitchDocument, PitchHistoryEntry, PitchProfile } from './types'

export type { DataSource, PitchDocument, PitchHistoryEntry, PitchProfile } from './types'

export interface StartPitchViewProps {
  sessionsAvailable: number
  /**
   * Whether the founder can start a session right now — true when prepaid
   * sessions remain OR the shared wallet balance covers the price. Drives the
   * start-vs-top-up CTA independently of the raw prepaid count.
   */
  canStartSession: boolean
  profile: PitchProfile
  documents: PitchDocument[]
  previousPitches: PitchHistoryEntry[]
  dataSources: DataSource[]
  /** Called when user clicks to watch the demo video */
  onPlayDemo?: () => void
  onUpdateDataSources?: () => void
  onStartPitch?: () => void
  onBuySession?: () => void
  isStartingPitch?: boolean
  promoSession?: boolean
}

export function StartPitchView({
  sessionsAvailable,
  canStartSession,
  profile,
  documents,
  previousPitches,
  dataSources,
  onPlayDemo,
  onUpdateDataSources,
  onStartPitch,
  onBuySession,
  isStartingPitch,
  promoSession,
}: StartPitchViewProps) {
  const missingRequired = dataSources.find((s) => s.required && !s.available)
  const canStart = !missingRequired && canStartSession
  const disabledReason = missingRequired ? `${missingRequired.label} is required` : undefined

  return (
    // Fills its container — the start-session dialog provides the width,
    // padding and title (DialogHeader), so no own heading or max-width here.
    <div className="w-full">
      {/* What to expect */}
      <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
        <li className="flex items-start gap-2.5">
          <Mic className="mt-1 size-3.5 shrink-0 text-[#f97316]" />
          <span>
            The AI Session is a recorded 30-minute conversation where you walk through your startup.
            You pitch for 10 minutes, then 20 minutes of Q&A. The AI asks the same questions
            investors ask in early meetings.
          </span>
        </li>
        <li className="flex items-start gap-2.5">
          <Target className="mt-1 size-3.5 shrink-0 text-[#f97316]" />
          <span>
            When you're done, your startup is scored through four distinct investor lenses, each
            analyzing your team, market, traction, and vision differently. Your Dashboard updates
            with a full breakdown of strengths, gaps, risks, and what to work on next. You can share
            the recording with investors as part of your SeedForge Profile.
          </span>
        </li>
        {onPlayDemo && (
          <li className="flex items-start gap-2.5">
            <Play className="mt-1 size-3.5 shrink-0 text-[#f97316]" />
            <span>
              Watch an{' '}
              <button
                type="button"
                onClick={onPlayDemo}
                className="inline font-medium text-[#f97316] underline underline-offset-2 transition-colors hover:text-[#f97316]/80"
              >
                example AI Session
              </button>{' '}
              to see what it looks like.
            </span>
          </li>
        )}
      </ul>

      {/* Tip */}
      <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] px-4 py-3">
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-500" />
        <div className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Tip:</span> Add FAQ, Business Plan, or White
          Paper to your profile to improve the accuracy of the evaluation.
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-border bg-card/30">
        <ProfileSummary profile={profile} />
        <Separator />
        <DataSourcesSection
          dataSources={dataSources}
          documents={documents}
          onUpdate={onUpdateDataSources}
        />
        {previousPitches.length > 0 && (
          <>
            <Separator />
            <PreviousPitchesSection pitches={previousPitches} />
          </>
        )}
      </div>

      <PitchCta
        sessionsAvailable={sessionsAvailable}
        canStartSession={canStartSession}
        canStart={canStart}
        disabledReason={disabledReason}
        onStartPitch={onStartPitch}
        onBuySession={onBuySession}
        isLoading={isStartingPitch}
        promoSession={promoSession}
      />
    </div>
  )
}
